import { FC, Fragment, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { mapAgeToColor } from '../../helpers';
import TicketsRefinement from '../TicketsRefinement/TicketsRefinement';
import TicketsCardViewLoader from './components/TicketsCardViewLoader';
import {
  CardStyled,
  CardViewStyled,
  FiltersWrapStyled,
  FlexRowItemStyled,
  FlexRowStyled,
  TextDetailsStyled,
  TextRoNumberStyled,
  TextTitleStyled,
} from './TicketsCardView.styled';
import DotsLoader from 'components/DotsLoader/DotsLoader';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { NoopT } from 'declarations/types/common';

export type TicketsCardViewPropsT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  isLoadingTickets: boolean;
  onSelectTicket: (id: string) => NoopT;
};

const TicketsCardView: FC<TicketsCardViewPropsT> = ({
  ticketsPages,
  isLoadingTickets,
  onSelectTicket,
}) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const { isFetchingNextTicketsPage, isFetchingTickets, hasNextTicketsPage, fetchNextTicketsPage } =
    useTickets();
  const { ref, inView } = useInView();
  const lastTicketObserver = ref;
  const shouldLoadMore = isFetchingTickets && isFetchingNextTicketsPage && hasNextTicketsPage;

  /**
   * Effects
   */
  useEffect(() => {
    if (inView && hasNextTicketsPage) {
      fetchNextTicketsPage();
    }
  }, [fetchNextTicketsPage, hasNextTicketsPage, inView]);

  return (
    <>
      <FiltersWrapStyled>
        <TicketsRefinement />
      </FiltersWrapStyled>
      {isLoadingTickets && !isFetchingNextTicketsPage ? (
        <TicketsCardViewLoader />
      ) : (
        <CardViewStyled data-testid="tickets-cards">
          {ticketsPages?.map((page, index) => (
            <Fragment key={`page-${index}`}>
              {page.data?.map(({ id, roNumber, age, assigneeName, lineNumber }) => (
                <CardStyled key={`${id}`} onClick={onSelectTicket(id)}>
                  {!isMobile && (
                    <div>
                      <TextTitleStyled variant="body5">RO</TextTitleStyled>
                      <TextRoNumberStyled variant="body3">{roNumber}</TextRoNumberStyled>
                    </div>
                  )}
                  <FlexRowStyled>
                    {isMobile && (
                      <FlexRowItemStyled>
                        <TextTitleStyled variant="body5">RO</TextTitleStyled>
                        <TextRoNumberStyled variant="body3">{roNumber}</TextRoNumberStyled>
                      </FlexRowItemStyled>
                    )}
                    <FlexRowItemStyled>
                      <TextTitleStyled variant="body5">Line</TextTitleStyled>
                      <TextDetailsStyled variant="body3"> {lineNumber || '-'}</TextDetailsStyled>
                    </FlexRowItemStyled>
                    <FlexRowItemStyled>
                      <TextTitleStyled variant="body5">Age (Days)</TextTitleStyled>
                      <TextDetailsStyled variant="body3" color={mapAgeToColor(age)}>
                        {age}
                      </TextDetailsStyled>
                    </FlexRowItemStyled>
                    <FlexRowItemStyled>
                      <TextTitleStyled variant="body5">Assignee</TextTitleStyled>
                      <TextDetailsStyled variant="body3">{assigneeName}</TextDetailsStyled>
                    </FlexRowItemStyled>
                  </FlexRowStyled>
                </CardStyled>
              ))}
            </Fragment>
          ))}
        </CardViewStyled>
      )}
      <span ref={lastTicketObserver} />
      {shouldLoadMore && (
        <FlexContainer justify="around">
          <DotsLoader />
        </FlexContainer>
      )}
    </>
  );
};

export default TicketsCardView;
